import { ROUTE_ANALYTICS_KNM } from 'CorePackage/MainModule/routes/routes';

import { handleResponse } from './handleResponse';
import { apiMockSwitcher } from '../mock/apiMockSwitcher';

interface IApiFetch {
  url: string;
  body?: Object | null;
  isAccept?: boolean;
  isCredentials?: boolean;
  authorization?: string;
}

export async function apiGet<T>(url: string, isAccept?: boolean, isExceptionResponse?: boolean): Promise<T> {
  const mockData = apiMockSwitcher<T>(url, 'GET');

  if (mockData) return mockData;

  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      ...(isAccept && { Accept: 'application/json' }),
    },
    referrerPolicy: window.location.href.includes(ROUTE_ANALYTICS_KNM) ? 'no-referrer' : 'origin',
  }).then(res => handleResponse(res, isExceptionResponse));
}

export async function apiGetBlob(url: string, isExceptionResponse?: boolean): Promise<Blob> {
  const mockData = apiMockSwitcher<Blob>(url, 'GET');

  if (mockData) return mockData;

  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    referrerPolicy: window.location.href.includes(ROUTE_ANALYTICS_KNM) ? 'no-referrer' : 'origin',
  });

  if (!res.ok) {
    return handleResponse(res, isExceptionResponse) as Promise<never>;
  }

  return res.blob();
}

async function apiFetch<T>({
  method,
  url,
  authorization,
  body,
  isAccept,
  isCredentials = true,
}: IApiFetch & { method: string }): Promise<T> {
  const isFormData = body instanceof FormData;
  const mockData = apiMockSwitcher<T>(url, method);

  if (mockData) return mockData;

  return fetch(url, {
    method,
    ...(isCredentials && { credentials: 'include' }),
    headers: {
      ...(isAccept && { Accept: 'application/json' }),
      ...(authorization && { Authorization: authorization }),
      ...(body && !isFormData && { 'Content-Type': 'application/json' }),
    },
    body: isFormData ? body : JSON.stringify(body),
    referrerPolicy: window.location.href.includes(ROUTE_ANALYTICS_KNM) ? 'no-referrer' : 'origin',
  }).then(handleResponse);
}

export async function apiPost<T>({ url, body, isAccept, authorization, isCredentials }: IApiFetch): Promise<T> {
  return apiFetch({ method: 'POST', url, body, isAccept, authorization, isCredentials });
}

export async function apiPatch<T>(url: string, body?: Object): Promise<T> {
  return apiFetch({ method: 'PATCH', url, body });
}

export async function apiPut<T>(url: string, body?: Object): Promise<T> {
  return apiFetch({ method: 'PUT', url, body });
}

export async function apiDelete<T>(url: string, body?: Object): Promise<T> {
  return apiFetch({ method: 'DELETE', url, body });
}

export async function apiGetWithAuth<T>({ url, authorization, isAccept, isCredentials }: IApiFetch): Promise<T> {
  return apiFetch({ method: 'GET', url, authorization, isAccept, isCredentials });
}

async function apiFetchZip({ method, url, authorization, body, isCredentials = true }: IApiFetch & { method: string }) {
  const isFormData = body instanceof FormData;

  return fetch(url, {
    method,
    ...(isCredentials && { credentials: 'include' }),
    headers: {
      ...(authorization && { Authorization: authorization }),
      'Content-Type': 'application/json',
      'Accept-Encoding': 'gzip, deflate',
    },
    body: isFormData ? body : JSON.stringify(body),
    referrerPolicy: window.location.href.includes(ROUTE_ANALYTICS_KNM) ? 'no-referrer' : 'origin',
  });
}

export async function apiPostZip({ url, body, authorization, isCredentials }: IApiFetch) {
  return apiFetchZip({ method: 'POST', url, body, authorization, isCredentials });
}
